import React, {useEffect, useMemo, useState} from 'react';
import {Grid} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {ReduxState} from '../../store/types';
import {actions} from '../../store';
import {RingGroupType} from '../../store/types/RingGroup';
import {UpdateHuntgroupsSubscriptionPayload} from '../../store/actions/accountDashboard/payloads';
import Header from '../../components/ListViewHeader/Header';
import TextField from '../../components/TextField/TextField';
import Button from '../../components/Button/Button';
import DataGrid from '../../components/DataGrid/DataGrid';
import {PaginationMode} from '../../components/DataGrid/types';
import AccountControlPanel from '../AccountControlPanel/AccountControlPanel';
import {generateColumns, useStyles} from './AccountRingGroups.utils';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import AccessDenied from '../../components/PermissionProvider/AccessDenied';
import {usePermissions} from '../../hooks/usePermissions';
import {AccountPermission, PermissionType} from '../../store/types/Permission';

const AccountRingGroups: React.VFC = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {ringGroups, isLoadingRingGroups} = useSelector(
        (state: ReduxState) => state.accountDashboard,
    );

    const {accountDetails} = useSelector(
        (state: ReduxState) => state.accountControlPanel,
    );

    const [numberFilter, setNumberFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [appliedNumberFilter, setAppliedNumberFilter] = useState('');
    const [appliedNameFilter, setAppliedNameFilter] = useState('');

    const permission = usePermissions(...AccountPermission.MyRingGroups.value);

    const fetchData = () => {
        dispatch(actions.getAccountDashboardRingGroupList.request());
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filteredGroups = useMemo<RingGroupType[]>(() => {
        const list = ringGroups ?? [];
        return list.filter((rg) => {
            const matchNumber =
                !appliedNumberFilter ||
                (rg.id ?? '').includes(appliedNumberFilter);
            const matchName =
                !appliedNameFilter ||
                (rg.name ?? '')
                    .toLowerCase()
                    .includes(appliedNameFilter.toLowerCase());
            return matchNumber && matchName;
        });
    }, [ringGroups, appliedNumberFilter, appliedNameFilter]);

    const handleLoginToggle = (rg: RingGroupType, value: boolean) => {
        const payload: UpdateHuntgroupsSubscriptionPayload = {
            i_account: accountDetails?.i_account,
            subscribe_to_huntgroups: value ? [{id: rg.id}] : undefined,
            unsubscribe_from_huntgroups: value ? undefined : [{id: rg.id}],
            callback: () => {
                dispatch(actions.getAccountDashboardRingGroupList.request());
            },
        };
        dispatch(actions.updateHuntgroupsSubscription.request(payload));
    };

    const columns = useMemo(
        () => generateColumns(t, classes, handleLoginToggle),
        [t, classes, accountDetails?.i_account],
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setAppliedNumberFilter(numberFilter);
        setAppliedNameFilter(nameFilter);
        fetchData();
    };

    if (permission == PermissionType.Hidden) {
        return <AccessDenied />;
    }

    return (
        <PermissionProvider permission={AccountPermission.MyRingGroups.value}>
        <div className={classes.mainDashboardContainer}>
            <div className={classes.header}>
                <Header
                    title={t<string>('screens:accountDashboard.myRingGroups')}
                    totalNumber={filteredGroups.length}
                    dataQa="account-ring-groups-header"
                    showDetails={false}
                />
                <div className={classes.rightSection}>
                    <AccountControlPanel />
                </div>
            </div>

            <div className={classes.scrollable}>
              <div className={classes.contentContainer}>
                <form
                    className={classes.filtersForm}
                    onSubmit={handleSubmit}
                    autoComplete="off"
                    data-testid="account-ring-groups-form"
                >
                    <Grid item className={classes.itemsContainer}>
                        <TextField
                            id="number"
                            label={t<string>('common:number')}
                            value={numberFilter}
                            onChange={(e) =>
                                setNumberFilter(e.target.value)
                            }
                            dataQa="account-rg-filter-number"
                            skipPermission
                        />
                        <TextField
                            id="name"
                            label={t<string>('common:name')}
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            dataQa="account-rg-filter-name"
                            skipPermission
                        />
                        <Button
                            primary
                            accent
                            type="submit"
                            dataQa="account-rg-search-button"
                            className={classes.button}
                            skipPermission
                        >
                            {t<string>('common:search')}
                        </Button>
                    </Grid>
                </form>

                <DataGrid<RingGroupType>
                    // @ts-ignore
                    className={classes.table}
                    columns={columns}
                    data={filteredGroups}
                    rowCount={filteredGroups.length}
                    loading={isLoadingRingGroups}
                    paginationMode={PaginationMode.Client}
                    initialPageSize={10}
                    centeredRows
                    narrowRows
                    getItemId={(rg) => rg.i_c_group}
                />
              </div>
            </div>
        </div>
        </PermissionProvider>
    );
};

export default AccountRingGroups;